import { get } from "helpers/api_helper"
import { call, put, take, takeEvery } from "redux-saga/effects"
import { getDownloadNameKey, showToast } from "utils"
import {
  downloadAttendeeListFailed,
  downloadAttendeeListSuccess,
  downloadAxisCareReportsFailed,
  downloadAxisCareReportsSuccess,
  downloadBookingPaymentsFailed,
  downloadBookingPaymentsSuccess,
  downloadBookingReportsFailed,
  downloadBookingReportsSuccess,
  downloadBookingUserReportsFailed,
  downloadBookingUserReportsSuccess,
  downloadCalendarGroupReportsFailed,
  downloadCalendarGroupReportsSuccess,
  downloadCompleted,
  downloadDeeplinkCodesFailed,
  downloadDeeplinkCodesSuccess,
  downloadFormPaymentsFailed,
  downloadFormPaymentsSuccess,
  downloadQuestionnaireScoresFailed,
  downloadQuestionnaireScoresSuccess,
  downloadReferFriendReportFailed,
  downloadReferFriendReportSuccess,
  downloadResponsesReportFailed,
  downloadResponsesReportSuccess,
  downloadSubCalendarReportsFailed,
  downloadSubCalendarReportsSuccess,
  downloadUsersListFailed,
  downloadUsersListSuccess,
  downloadViewsByUserFailed,
  downloadViewsByUserSuccess,
  getDownloadsFailed,
  getDownloadsSuccess,
  openDownloadCompletedModal,
} from "./actions"
import {
  DOWNLOAD_ATTENDEE_LIST,
  DOWNLOAD_AXISCARE_REPORTS,
  DOWNLOAD_BOOKING_PAYMENTS,
  DOWNLOAD_BOOKING_REPORTS,
  DOWNLOAD_BOOKING_USER_REPORTS,
  DOWNLOAD_CALENDAR_GROUP_REPORTS,
  DOWNLOAD_DEEPLINK_CODES,
  DOWNLOAD_FORM_PAYMENTS,
  DOWNLOAD_QUESTIONNAIRE_SCORES,
  DOWNLOAD_REFER_FRIEND_REPORT,
  DOWNLOAD_RESPONSES_REPORT,
  DOWNLOAD_SUB_CALENDAR_REPORTS,
  DOWNLOAD_USERS_LIST,
  DOWNLOAD_VIEWS_BY_USER,
  GET_DOWNLOADS,
} from "./actionTypes"
import { bindEvent } from "services/pusher"
import { getCurrentTimeZone } from "containers/Communications/utils"
import { eventChannel } from "redux-saga"
import * as url from "helpers/url_helper"

function* getDownloads({ payload: params }) {
  try {
    const response = yield get("/download", params)
    yield put(getDownloadsSuccess(response))
  } catch (error) {
    yield put(getDownloadsFailed(error))
    showToast("error", error, "Error")
  }
}

function bindToDownload(download) {
  return eventChannel(emit => {
    bindEvent(getDownloadNameKey(download), data => {
      emit(data)
    })
    return () => {}
  })
}

function* downloadResponsesReport({ payload: params }) {
  try {
    let p = {
      download: 1,
      timezone: getCurrentTimeZone(),
      feature: params?.feature,
      feature_id: params?.feature_id,
      page: "" || params?.page,
      per_page: "" || params?.per_page,
      report_name: params?.report_name,
    }
    if (params?.start) {
      p.start = params?.start
    }
    if (params?.end) {
      p.end = params?.end
    }
    if (params?.answer_id) {
      p.answer_id = params?.answer_id
    }
    const download = yield get("/feature/responsesByUser", { params: p })
    showToast(
      "info",
      "You will be alerted when it is ready.",
      "Your list is now downloading!"
    )
    const channel = yield call(bindToDownload, download)
    while (true) {
      try {
        const resp = yield take(channel)
        yield put(downloadCompleted(resp))
        yield put(openDownloadCompletedModal())
        yield put(downloadResponsesReportSuccess())
      } catch (error) {
        console.log("ERROR", error)
      }
    }
  } catch (error) {
    yield put(downloadResponsesReportFailed(error))
    showToast("error", error, "Error")
  }
}

function* downloadQuestionnaireScores({ payload: params }) {
  try {
    let p = {
      download: 1,
      timezone: getCurrentTimeZone(),
      questionnaire_id: params?.questionnaire_id,
      report_name: params?.report_name,
    }
    if (params?.start) {
      p.start = params?.start
    }
    if (params?.end) {
      p.end = params?.end
    }
    if (params?.s) {
      p.s = params?.s
    }
    if (params?.result_set_id) {
      p.result_set_id = params?.result_set_id
    }
    const download = yield get("/admin/questionnaire-scores", { params: p })
    showToast(
      "info",
      "You will be alerted when it is ready.",
      "Your list is now downloading!"
    )
    const channel = yield call(bindToDownload, download)
    while (true) {
      try {
        const resp = yield take(channel)
        yield put(downloadCompleted(resp))
        yield put(openDownloadCompletedModal())
        yield put(downloadQuestionnaireScoresSuccess())
      } catch (error) {
        console.log("ERROR", error)
      }
    }
  } catch (error) {
    yield put(downloadQuestionnaireScoresFailed(error))
    showToast("error", error, "Error")
  }
}

function* downloadBookingPayments({ payload: params }) {
  try {
    const download = yield get("/admin/booking-payments", { params })
    showToast(
      "info",
      "You will be alerted when it is ready.",
      "Your list is now downloading!"
    )
    const channel = yield call(bindToDownload, download)
    while (true) {
      try {
        const resp = yield take(channel)
        yield put(downloadCompleted(resp))
        yield put(openDownloadCompletedModal())
        yield put(downloadBookingPaymentsSuccess())
      } catch (error) {
        console.log("ERROR", error)
      }
    }
  } catch (error) {
    yield put(downloadBookingPaymentsFailed(error))
    showToast("error", error, "Error")
  }
}

function* downloadFormPayments({ payload: params }) {
  try {
    const download = yield get("/admin/form-builder-payments", { params })
    showToast(
      "info",
      "You will be alerted when it is ready.",
      "Your list is now downloading!"
    )
    const channel = yield call(bindToDownload, download)
    while (true) {
      try {
        const resp = yield take(channel)
        yield put(downloadCompleted(resp))
        yield put(openDownloadCompletedModal())
        yield put(downloadFormPaymentsSuccess())
      } catch (error) {
        console.log("ERROR", error)
      }
    }
  } catch (error) {
    yield put(downloadFormPaymentsFailed(error))
    showToast("error", error, "Error")
  }
}

function* downloadViewsByUser({ payload: params }) {
  try {
    const download = yield get("/feature/viewsByUser", { params })
    showToast(
      "info",
      "You will be alerted when it is ready.",
      "Your list is now downloading!"
    )
    const channel = yield call(bindToDownload, download)
    while (true) {
      try {
        const resp = yield take(channel)
        yield put(downloadCompleted(resp))
        yield put(openDownloadCompletedModal())
        yield put(downloadViewsByUserSuccess())
      } catch (error) {
        console.log("ERROR", error)
      }
    }
  } catch (error) {
    yield put(downloadViewsByUserFailed(error))
    showToast("error", error, "Error")
  }
}

function* downloadReferFriendReport({ payload: params }) {
  try {
    const download = yield get("/admin/refer-friend/reports", { params })
    showToast(
      "info",
      "You will be alerted when it is ready.",
      "Your list is now downloading!"
    )
    const channel = yield call(bindToDownload, download)
    while (true) {
      try {
        const resp = yield take(channel)
        yield put(downloadCompleted(resp))
        yield put(openDownloadCompletedModal())
        yield put(downloadReferFriendReportSuccess())
      } catch (error) {
        console.log("ERROR", error)
      }
    }
  } catch (error) {
    yield put(downloadReferFriendReportFailed(error))
    showToast("error", error, "Error")
  }
}

function* downloadAttendeeList({ payload: params }) {
  try {
    const download = yield get(
      `/admin/invite/vendor/${params?.vendor_id}/event/${params?.event_id}`,
      {
        params: params?.values,
      }
    )
    showToast(
      "info",
      "You will be alerted when it is ready.",
      "Your list is now downloading!"
    )
    const channel = yield call(bindToDownload, download)
    while (true) {
      try {
        const resp = yield take(channel)
        yield put(downloadCompleted(resp))
        yield put(openDownloadCompletedModal())
        yield put(downloadAttendeeListSuccess())
      } catch (error) {
        console.log("ERROR", error)
      }
    }
  } catch (error) {
    yield put(downloadAttendeeListFailed(error))
    showToast("error", error, "Error")
  }
}

function* downloadCalendarGroupReports({ payload: params }) {
  try {
    const download = yield get(url.CALENDAR_GROUP_REPORTS_URL, {
      params,
    })
    showToast(
      "info",
      "You will be alerted when it is ready.",
      "Your list is now downloading!"
    )
    const channel = yield call(bindToDownload, download)
    while (true) {
      try {
        const resp = yield take(channel)
        yield put(downloadCompleted(resp))
        yield put(openDownloadCompletedModal())
        yield put(downloadCalendarGroupReportsSuccess())
      } catch (error) {
        console.log("ERROR", error)
      }
    }
  } catch (error) {
    yield put(downloadCalendarGroupReportsFailed(error))
    showToast("error", error, "Error")
  }
}

function* downloadSubCalendarReports({ payload: params }) {
  try {
    const download = yield get(url.SUB_CALENDAR_REPORTS_URL, {
      params,
    })
    showToast(
      "info",
      "You will be alerted when it is ready.",
      "Your list is now downloading!"
    )
    const channel = yield call(bindToDownload, download)
    while (true) {
      try {
        const resp = yield take(channel)
        yield put(downloadCompleted(resp))
        yield put(openDownloadCompletedModal())
        yield put(downloadSubCalendarReportsSuccess())
      } catch (error) {
        console.log("ERROR", error)
      }
    }
  } catch (error) {
    yield put(downloadSubCalendarReportsFailed(error))
    showToast("error", error, "Error")
  }
}

function* downloadBookingUserReports({ payload: params }) {
  try {
    const download = yield get(url.USERS_BOOKING_URL, {
      params,
    })
    showToast(
      "info",
      "You will be alerted when it is ready.",
      "Your list is now downloading!"
    )
    const channel = yield call(bindToDownload, download)
    while (true) {
      try {
        const resp = yield take(channel)
        yield put(downloadCompleted(resp))
        yield put(openDownloadCompletedModal())
        yield put(downloadBookingUserReportsSuccess())
      } catch (error) {
        console.log("ERROR", error)
      }
    }
  } catch (error) {
    yield put(downloadBookingUserReportsFailed(error))
    showToast("error", error, "Error")
  }
}

function* downloadBookingReports({ payload: params }) {
  try {
    const download = yield get(url.BOOKING_REPORTS_URL, {
      params,
    })
    showToast(
      "info",
      "You will be alerted when it is ready.",
      "Your list is now downloading!"
    )
    const channel = yield call(bindToDownload, download)
    while (true) {
      try {
        const resp = yield take(channel)
        yield put(downloadCompleted(resp))
        yield put(openDownloadCompletedModal())
        yield put(downloadBookingReportsSuccess())
      } catch (error) {
        console.log("ERROR", error)
      }
    }
  } catch (error) {
    yield put(downloadBookingReportsFailed(error))
    showToast("error", error, "Error")
  }
}

function* downloadUsersList({ payload: params }) {
  try {
    const download = yield get(url.GET_CUSTOMER_URL, {
      params,
    })
    showToast(
      "info",
      "You will be alerted when it is ready.",
      "Your list is now downloading!"
    )
    const channel = yield call(bindToDownload, download)
    while (true) {
      try {
        const resp = yield take(channel)
        yield put(downloadCompleted(resp))
        yield put(openDownloadCompletedModal())
        yield put(downloadUsersListSuccess())
      } catch (error) {
        console.log("ERROR", error)
      }
    }
  } catch (error) {
    yield put(downloadUsersListFailed(error))
    showToast("error", error, "Error")
  }
}

function* downloadDeeplinkCodes({ payload: params }) {
  try {
    const download = yield get(url.DEEPLINK_CODES_URL, {
      params,
    })
    showToast(
      "info",
      "You will be alerted when it is ready.",
      "Your list is now downloading!"
    )
    const channel = yield call(bindToDownload, download)
    while (true) {
      try {
        const resp = yield take(channel)
        yield put(downloadCompleted(resp))
        yield put(openDownloadCompletedModal())
        yield put(downloadDeeplinkCodesSuccess())
      } catch (error) {
        console.log("ERROR", error)
      }
    }
  } catch (error) {
    yield put(downloadDeeplinkCodesFailed(error))
    showToast("error", error, "Error")
  }
}

function* downloadAxisCareReports({ payload: params }) {
  try {
    const download = yield get(url.AXISCARE_REPORTS_URL, {
      params,
    })
    showToast(
      "info",
      "You will be alerted when it is ready.",
      "Your list is now downloading!"
    )
    const channel = yield call(bindToDownload, download)
    while (true) {
      try {
        const resp = yield take(channel)
        yield put(downloadCompleted(resp))
        yield put(openDownloadCompletedModal())
        yield put(downloadAxisCareReportsSuccess())
      } catch (error) {
        console.log("ERROR", error)
      }
    }
  } catch (error) {
    yield put(downloadAxisCareReportsFailed(error))
    showToast("error", error, "Error")
  }
}

function* downloadsSaga() {
  yield takeEvery(GET_DOWNLOADS, getDownloads)
  yield takeEvery(DOWNLOAD_RESPONSES_REPORT, downloadResponsesReport)
  yield takeEvery(DOWNLOAD_QUESTIONNAIRE_SCORES, downloadQuestionnaireScores)
  yield takeEvery(DOWNLOAD_BOOKING_PAYMENTS, downloadBookingPayments)
  yield takeEvery(DOWNLOAD_FORM_PAYMENTS, downloadFormPayments)
  yield takeEvery(DOWNLOAD_VIEWS_BY_USER, downloadViewsByUser)
  yield takeEvery(DOWNLOAD_REFER_FRIEND_REPORT, downloadReferFriendReport)
  yield takeEvery(DOWNLOAD_ATTENDEE_LIST, downloadAttendeeList)
  yield takeEvery(DOWNLOAD_CALENDAR_GROUP_REPORTS, downloadCalendarGroupReports)
  yield takeEvery(DOWNLOAD_SUB_CALENDAR_REPORTS, downloadSubCalendarReports)
  yield takeEvery(DOWNLOAD_BOOKING_USER_REPORTS, downloadBookingUserReports)
  yield takeEvery(DOWNLOAD_BOOKING_REPORTS, downloadBookingReports)
  yield takeEvery(DOWNLOAD_USERS_LIST, downloadUsersList)
  yield takeEvery(DOWNLOAD_DEEPLINK_CODES, downloadDeeplinkCodes)
  yield takeEvery(DOWNLOAD_AXISCARE_REPORTS, downloadAxisCareReports)
}

export default downloadsSaga
